import * as React from 'react';
import { useKeyManager } from './useKeyManager.js';
export { UseKeyManagerDirection as UseFocusManagerDirection, UseKeyManagerOrientation as UseFocusManagerOrientation } from './useKeyManager.js';
import { useSSRFocusState } from '../useSSRFocusState.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var UseFocusManagerFocusControl;

(function (UseFocusManagerFocusControl) {
  UseFocusManagerFocusControl["Virtual"] = "Virtual";
  UseFocusManagerFocusControl["TabIndex"] = "TabIndex";
})(UseFocusManagerFocusControl || (UseFocusManagerFocusControl = {}));

// This function will update the scrollposition of the container as the
// current item with visual focus moves out of the container frame
// Pulled from WAI APG Listbox example -> https://www.w3.org/WAI/ARIA/apg/example-index/listbox/js/listbox.js
var updateScroll = function updateScroll(activeItem, listbox) {
  if (!activeItem || !listbox) {
    return; // early return if we don't have what we need
  }

  if (activeItem && listbox.scrollHeight > listbox.clientHeight) {
    var scrollBottom = listbox.clientHeight + listbox.scrollTop;
    var elementBottom = activeItem.offsetTop + activeItem.offsetHeight;

    if (elementBottom > scrollBottom) {
      listbox.scrollTop = elementBottom - listbox.clientHeight;
    } else if (activeItem.offsetTop < listbox.scrollTop) {
      listbox.scrollTop = activeItem.offsetTop;
    }
  }
};

var useFocusManager = function useFocusManager(_ref) {
  var _ref$focusControl = _ref.focusControl,
      focusControl = _ref$focusControl === void 0 ? UseFocusManagerFocusControl.Virtual : _ref$focusControl,
      _ref$initialActiveIte = _ref.initialActiveItem,
      initialActiveItem = _ref$initialActiveIte === void 0 ? null : _ref$initialActiveIte,
      _ref$itemPropGenerato = _ref.itemPropGenerator,
      itemPropGenerator = _ref$itemPropGenerato === void 0 ? function (_ref2) {
    var index = _ref2.index;
    return [index.toString(), {}];
  } : _ref$itemPropGenerato,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      items = _ref.items,
      _onActiveItemChange = _ref.onActiveItemChange,
      passInitialFocusOnMount = _ref.passInitialFocusOnMount,
      props = _objectWithoutProperties(_ref, ["focusControl", "initialActiveItem", "itemPropGenerator", "disabled", "items", "onActiveItemChange", "passInitialFocusOnMount"]);

  var containerRef = React.useRef(null);

  var _useSSRFocusState = useSSRFocusState(containerRef),
      _useSSRFocusState2 = _slicedToArray(_useSSRFocusState, 2),
      containerHasFocus = _useSSRFocusState2[0],
      setContainerFocus = _useSSRFocusState2[1]; // An array to store item element refs


  var itemsRef = React.useRef([]);
  var usingVirtualFocus = focusControl === UseFocusManagerFocusControl.Virtual;

  var _useKeyManager = useKeyManager(_objectSpread({
    items: items,
    disableManager: disabled,
    onActiveItemChange: function onActiveItemChange(event) {
      var _event$active$item;

      if (disabled) {
        return;
      } // use the active index to fetch the ref of the item's DOM element to either
      // 1) pass focus to the element when roving tab index is used
      // 2) scroll the element into view if it is outside the container when virtual tabbing is used


      // use the active index to fetch the ref of the item's DOM element to either
      // 1) pass focus to the element when roving tab index is used
      // 2) scroll the element into view if it is outside the container when virtual tabbing is used
      var index = event.active.index;
      var currentItemRef = itemsRef.current[index] ? itemsRef.current[index] : // if refs aren't being forwarded properly, try to use querySelector as a fallback
      document.querySelector('#' + ((_event$active$item = event.active.item) === null || _event$active$item === void 0 ? void 0 : _event$active$item.id)); // If roving tabindex is being used, move focus to the new active item

      // If roving tabindex is being used, move focus to the new active item
      if (!usingVirtualFocus) {
        // giving DOM a chance to be settled
        requestAnimationFrame(function () {
          currentItemRef.focus();
        });
      } // If virtual focus is used, we need to scroll the container if the element is out of view


      // If virtual focus is used, we need to scroll the container if the element is out of view
      if (usingVirtualFocus) {
        updateScroll(currentItemRef, containerRef.current);
      }

      if (typeof _onActiveItemChange === 'function') {
        _onActiveItemChange(event);
      }
    }
  }, props)),
      activeItem = _useKeyManager.activeItem,
      activeItemIndex = _useKeyManager.activeItemIndex,
      updateActiveItem = _useKeyManager.updateActiveItem,
      keyManagerControlProps = _useKeyManager.controlProps,
      keyManagerProps = _objectWithoutProperties(_useKeyManager, ["activeItem", "activeItemIndex", "updateActiveItem", "controlProps"]); // on initial mount set an inital active element


  React.useEffect(function () {
    // Passing false as second argument so our `onActiveItemChange` function above doesn't run
    // We don't want to trigger focus or any other side effects on the initial load
    updateActiveItem(initialActiveItem === null && !usingVirtualFocus ? -1 : initialActiveItem, passInitialFocusOnMount);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Reset our item refs when the items change

  React.useEffect(function () {
    itemsRef.current = [];
  }, [items]);
  var itemProps = {};
  items.forEach(function (item, index) {
    var itemOnClick = function itemOnClick(event) {
      updateActiveItem(index, false, event);
    };

    var _itemPropGenerator = itemPropGenerator({
      item: item,
      index: index,
      containerHasFocus: containerHasFocus,
      activeItemIndex: activeItemIndex,
      activeItem: activeItem,
      onClick: itemOnClick
    }),
        _itemPropGenerator2 = _slicedToArray(_itemPropGenerator, 2),
        key = _itemPropGenerator2[0],
        props = _itemPropGenerator2[1];

    itemProps[key ? key : index] = _objectSpread(_objectSpread({
      onClick: itemOnClick
    }, props), {}, {
      isFocused: containerHasFocus && activeItemIndex === index,
      // if virtual focus is used, return undefined
      tabIndex: !usingVirtualFocus ? index === activeItemIndex ? 0 : -1 : undefined,
      id: item.id,
      ref: function ref(el) {
        return itemsRef.current[index] = el;
      }
    });
  });
  return _objectSpread({
    activeItem: activeItem,
    activeItemIndex: activeItemIndex,
    updateActiveItem: updateActiveItem,
    itemProps: itemProps,
    isDisabled: disabled,
    containerProps: _objectSpread({
      onFocus: function onFocus() {
        setContainerFocus(true);
      },
      onBlur: function onBlur() {
        setContainerFocus(false);
      },
      ref: containerRef,
      tabIndex: usingVirtualFocus ? 0 : undefined,
      'aria-activedescendant': usingVirtualFocus ? activeItem && activeItem.id ? activeItem.id : '' : undefined
    }, keyManagerControlProps)
  }, keyManagerProps);
};

export { UseFocusManagerFocusControl, useFocusManager };
